"use client";

/**
 * Good-value listings in the residences around one residence (feedback
 * ticket 65: "if we search in Mayak Residence, below it the flats in that
 * building and, in a separate catalogue, the favourable flats near
 * there"). It sits under a one-residence search on /listings and under
 * the residence page's own listings: the regular listing cards, each
 * saying its residence and the distance to it, and one button to the
 * listings page with the circle around the residence and the deal tiers
 * (lib/nearby-deals). That page holds every deal in the circle, the
 * residence's own and those outside residences too (most of them, around
 * Mayak Residence: 146 of 159 on the scratch data), so the button says
 * "all good-value listings around" rather than "see all of these".
 *
 * The request goes out only when the block comes near the screen, once
 * per query (lib/api fetchNearbyDeals). Until cards arrive it takes no
 * room, and with none it never shows.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import Link from "@/lib/localized-link";
import WhenVisible from "@/components/WhenVisible";
import SectionHeader from "@/components/trends/SectionHeader";
import ListingBrowseCard from "./ListingBrowseCard";
import { fetchNearbyDeals } from "@/lib/api";
import { useHiddenIds } from "@/lib/hidden-listings";
import { useLocale, useT } from "@/lib/locale-context";
import { decimal, distanceParts, nearbyAllHref, nearbyApiParams, type NearbyQuery } from "@/lib/nearby-deals";
import { listingPath } from "@/lib/seo/paths";
import { trackEvent } from "@/lib/tracking";
import { BUTTON_SECONDARY, CHEVRON } from "@/lib/ui-classes";
import type { ListingCard, NearbyDealsResponse } from "@/types";

interface Props {
  query: NearbyQuery;
  /** Opens the page's own listing drawer, as its other cards do. */
  onOpen: (listing: ListingCard) => void;
  /** The card whose details are open (its ink outline). */
  activeId?: string | null;
  /** The page's grid columns, so these cards line up with its own. */
  gridClassName: string;
  /** The listings grid beside a docked drawer (narrower cards). */
  dense?: boolean;
  /** Cards shown (the API sends up to 12), and on phones. */
  max?: number;
  phoneMax?: number;
  /** "listings": a section like the landing pages' under the grid;
   *  "residence": the residence page's small section heading. */
  surface: "listings" | "residence";
  className?: string;
}

export default function NearbyDeals(props: Props) {
  return (
    <WhenVisible className="h-px" rootMargin="600px">
      <NearbyDealsBlock {...props} />
    </WhenVisible>
  );
}

function NearbyDealsBlock({
  query, onOpen, activeId = null, gridClassName, dense = false, max = 12, phoneMax, surface, className = "",
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const hiddenIds = useHiddenIds();
  const key = JSON.stringify(nearbyApiParams(query));
  const [data, setData] = useState<{ key: string; res: NearbyDealsResponse } | null>(null);

  useEffect(() => {
    let alive = true;
    fetchNearbyDeals(JSON.parse(key) as Record<string, string | string[]>)
      .then((res) => { if (alive) setData({ key, res }); })
      // Garnish: on a failure the block stays away.
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [key]);

  const items = (data?.res.items ?? []).filter((it) => !hiddenIds.has(it.card.id)).slice(0, max);
  const shownKey = data && items.length ? data.key : null;
  const tracked = useRef<string | null>(null);
  useEffect(() => {
    if (!shownKey || tracked.current === shownKey) return;
    tracked.current = shownKey;
    trackEvent("nearby_deals.shown", { surface, n: items.length, radius_km: data?.res.radius_km ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey]);

  if (!data || !items.length || !data.res.center || data.res.radius_km == null) return null;
  const { center, radius_km: radius } = data.res;
  // Another query is on its way (the rooms changed): the cards stay,
  // dimmed, until the new ones land, as the listings grid does.
  const stale = data.key !== key;
  const allHref = nearbyAllHref(query, center, radius);
  const km = Number.isInteger(radius) ? String(radius) : decimal(radius, locale);
  const note = t("listings.nearby_deals.note").replace("{km}", km);
  const onAll = () => trackEvent("nearby_deals.all", { surface, radius_km: radius });
  // The section's one way on, under the cards: a quiet button (the page's
  // indigo stays on its own primary action), full width on the residence
  // page like the "see all" above it, centred on /listings like "more".
  const allLink = (
    <Link
      href={allHref}
      onClick={onAll}
      className={`${surface === "residence"
        ? "flex w-full justify-center rounded-xl py-2.5"
        : "inline-flex h-10 rounded-lg"} items-center gap-1.5 px-4 text-sm font-semibold ${BUTTON_SECONDARY} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500`}
    >
      {t("listings.nearby_deals.all")}
      <ChevronRight className={`h-4 w-4 ${CHEVRON}`} aria-hidden />
    </Link>
  );

  const distance = (d: number) => {
    const p = distanceParts(d, locale);
    return t(p.unit === "m" ? "nearby.distance_m" : "nearby.distance_km").replace("{n}", p.n);
  };

  return (
    <section className={className} aria-label={t("listings.nearby_deals.title")}>
      {surface === "listings" ? (
        <>
          <SectionHeader title={t("listings.nearby_deals.title")} />
          <p className="-mt-2 mb-4 text-sm text-gray-500">{note}</p>
        </>
      ) : (
        <>
          <h2 className="mb-1 text-sm font-semibold text-gray-900">{t("listings.nearby_deals.title")}</h2>
          <p className="mb-3 text-xs text-gray-500">{note}</p>
        </>
      )}
      <div className={`grid gap-3 transition-opacity duration-300 ${gridClassName} ${stale ? "pointer-events-none opacity-40" : ""}`}>
        {items.map((it, i) => {
          const lp = listingPath(it.card.id);
          return (
            <div
              key={it.card.id}
              data-listing-card={surface === "listings" ? it.card.id : undefined}
              className={phoneMax != null && i >= phoneMax ? "hidden h-full sm:block" : "h-full"}
            >
              <ListingBrowseCard
                listing={it.card}
                onOpen={(l) => {
                  trackEvent("nearby_deals.open", { surface, rank: i + 1, km: Math.round(it.distance_km * 10) / 10 });
                  onOpen(l);
                }}
                href={lp ? `/${locale}${lp}` : undefined}
                dense={dense}
                active={activeId === it.card.id}
                // Both sides in one list only when the search asks for both.
                showTransaction={query.leased === null}
                priceNote={
                  <span className="mt-0.5 flex min-w-0 items-baseline gap-1 text-xs leading-snug">
                    <span className="min-w-0 truncate font-semibold text-gray-700" title={it.residence.name}>
                      {it.residence.name}
                    </span>
                    <span aria-hidden className="text-gray-400">·</span>
                    <span className="shrink-0 tabular-nums text-gray-500">{distance(it.distance_km)}</span>
                  </span>
                }
              />
            </div>
          );
        })}
      </div>
      {surface === "residence" ? <div className="mt-4">{allLink}</div> : <div className="mt-4 flex justify-center">{allLink}</div>}
    </section>
  );
}
